import { useEffect, useMemo, useState } from "react";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import type { HandMatchSignRules } from "../lib/handMatchSign";
import { BLOCK_COL, buildMatchListFrame, type MatchListEntry } from "../lib/matchListFrame";
import ColumnFilterGrid from "./ColumnFilterGrid";
import ConfirmDangerAction from "./ConfirmDangerAction";
import { fmtAed } from "./KpiCard";

interface Props {
  entries: MatchListEntry[];
  criFrame: FrameRecords;
  insFrame: FrameRecords;
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  insurerName?: string;                   // names the insurer band
  signRules?: HandMatchSignRules | null;  // how the books' legs must cancel
  title: string;
  resetKey: string;
  busy: boolean;
  actionLabel: string;                    // "Restore", "Forget", "Remove"
  describe: (n: number) => string;        // the confirmation question
  // A word to type before acting. Set for an action that deletes stored decisions;
  // omitted for a reversible one, which takes a plain confirm.
  typedWord?: string;
  onAction: (keys: string[]) => void;
}

// A stored-match list (session, remembered or released) as a PAIRED table: each line puts
// a CRI leg beside its insurer leg under Match | CRI | insurer bands, like Side-by-Side. A
// match spanning several lines (N:M, or a within-ledger offset) opens with a section row
// that foots both sides, so whether it ties is readable without adding anything up.
// Ticking ANY line selects its WHOLE match — every action here acts on matches.
export default function MatchListTable({
  entries, criFrame, insFrame, criConfig, insConfig, insurerName = "", signRules, title,
  resetKey, busy, actionLabel, describe, typedWord, onAction,
}: Props) {
  const grid = useMemo(
    () => buildMatchListFrame(entries, criFrame, insFrame, criConfig, insConfig, signRules),
    [entries, criFrame, insFrame, criConfig, insConfig, signRules]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Drop ticks for matches that left the list (an action, a re-finalize), so the count
  // on the button never includes a match that is no longer there.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => grid.rowsOfEntry.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [grid]);

  // What the search and column filters leave on screen (null = nothing is filtered).
  // THE ACTION ONLY EVER TOUCHES VISIBLE MATCHES: finance pasted document numbers,
  // selected what was listed, and the old table acted on every ticked match, hidden ones
  // included — so a filtered Forget could not be scoped to the filtered matches.
  const [visibleRows, setVisibleRows] = useState<string[] | null>(null);
  const visible = useMemo(() => {
    if (visibleRows === null) return null;
    const out = new Set<string>();
    for (const r of visibleRows) {
      const k = grid.entryOfRow.get(r);
      if (k !== undefined) out.add(k);
    }
    return out;
  }, [visibleRows, grid]);
  const shown = useMemo(
    () => (visible ? entries.filter((e) => visible.has(e.key)) : entries), [entries, visible]);
  const acting = useMemo(
    () => [...selected].filter((k) => !visible || visible.has(k)), [selected, visible]);
  const hiddenTicked = selected.size - acting.length;

  const selectedRows = useMemo(() => {
    const out = new Set<string>();
    for (const k of selected) for (const r of grid.rowsOfEntry.get(k) ?? []) out.add(r);
    return out;
  }, [selected, grid]);

  const setEntries = (rowKeys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of rowKeys) {
        const k = grid.entryOfRow.get(r);
        if (k === undefined) continue;
        if (on) next.add(k); else next.delete(k);
      }
      return next;
    });

  const n = acting.length;
  const all = shown.length > 0 && shown.every((e) => selected.has(e.key));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    for (const e of shown) if (all) next.delete(e.key); else next.add(e.key);
    return next;
  });
  const act = () => {
    const keys = acting;
    setSelected((prev) => new Set([...prev].filter((k) => !keys.includes(k))));
    onAction(keys);
  };
  const label = `${actionLabel} ${n} selected`;
  const noun = (k: number) => `match${k === 1 ? "" : "es"}`;

  // The section row opening a multi-line match. A single line already carries its own
  // Match #, Status and Residual, so it gets none.
  const blockHeader = (block: string) => {
    const b = grid.blocks.get(block);
    if (!b || b.lines < 2) return null;
    return (
      <>
        <span className="mblock-no">Match {b.matchNo}</span>
        <span className="badge">{b.shape}</span>
        <span className="muted">{b.status}</span>
        {!b.resolved ? (
          <span className="muted">
            {b.missing} row{b.missing === 1 ? " is" : "s are"} not in this upload, so the
            totals are unavailable
          </span>
        ) : (
          <>
            <span className="mblock-foot">
              {b.crossBook
                ? <>CRI {fmtAed(b.criTotal)} ⟷ {insurerName || "Insurer"} {fmtAed(b.insTotal)}</>
                : <>net {fmtAed(b.net)}</>}
            </span>
            <span className={b.ties ? "mgroup-verdict is-tied" : "mgroup-verdict"}>
              {b.ties ? "✓ settles exactly" : `residual ${fmtAed(b.residual)}`}
            </span>
          </>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="msearch-actions">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}>
          <input type="checkbox" disabled={busy || shown.length === 0} checked={all}
            ref={(el) => {
              if (el) el.indeterminate = !all && shown.some((e) => selected.has(e.key));
            }}
            onChange={toggleAll} />
          {visible
            ? `Select all ${shown.length} ${noun(shown.length)} shown`
            : `Select all ${entries.length} ${noun(entries.length)}`}
        </label>
        {typedWord ? (
          <ConfirmDangerAction label={label} disabled={busy || n === 0}
            title={describe(n)} body="Ticking any row of a match selects the whole match."
            confirmWord={typedWord} confirmLabel={label} onConfirm={act} />
        ) : (
          <button className="btn" disabled={busy || n === 0}
            onClick={() => { if (window.confirm(describe(n))) act(); }}>
            {label}
          </button>
        )}
        {hiddenTicked > 0 && (
          <span className="muted">
            {hiddenTicked} ticked {noun(hiddenTicked)} hidden by the search or filters
            {hiddenTicked === 1 ? " is" : " are"} left alone.
          </span>
        )}
      </div>
      <ColumnFilterGrid frame={grid.frame} sigColumn="_sig"
        selectedSig={null} selectedSigs={selectedRows}
        onSelect={(r) => r && setEntries([r], !selectedRows.has(r))}
        onSelectMany={setEntries}
        onFilteredChange={setVisibleRows}
        grouped groupKeyCol={BLOCK_COL} blockHeader={blockHeader}
        pinSide="paired" insurerName={insurerName} compact
        config={grid.config} resetKey={resetKey} title={title} />
    </div>
  );
}
